import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/shared/Button';
import TeamQRCodes from '../components/shared/TeamQRCodes';
import { HitsterEvent, EventRound, EventSong, EventTeam, GameRulesConfig, GameRulesScoringItem, GameRulesSlide, GameRulesSlideType, HitsterGenerationPeriodId } from '../types';
import { eventHasRoundScores, getEventTeamStandings } from '../utils/eventScores';
import { getDefaultGameRulesConfig, normalizeGameRulesConfig } from '../utils/gameRules';
import { sortRoundSongs } from '../utils/roundTimeline';
import './Events.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const TEAM_GENERATION_OPTIONS: Array<{ id: HitsterGenerationPeriodId; label: string }> = [
  { id: 'vinyl-classics', label: 'Vinyl' },
  { id: 'cassette-cd', label: 'Cassette/CD' },
  { id: 'streaming', label: 'Streaming' },
];

const formatGenerationCategory = (category?: HitsterGenerationPeriodId) => {
  return TEAM_GENERATION_OPTIONS.find((item) => item.id === category)?.label || null;
};

const getEventType = (event?: HitsterEvent | null): 'rounds' | 'theme-night' => {
  return event?.eventType === 'theme-night' ? 'theme-night' : 'rounds';
};

const getEventSongCount = (event?: HitsterEvent | null): number => {
  if (!event) return 0;
  if (getEventType(event) === 'theme-night') {
    return (event.themeSongs || []).length;
  }
  return (event.rounds || []).reduce((total, round) => total + (round.songs || []).length, 0);
};

const Events: React.FC = () => {
  const navigate = useNavigate();
  
  // Events state
  const [events, setEvents] = useState<HitsterEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<HitsterEvent | null>(null);
  const [selectedRound, setSelectedRound] = useState<EventRound | null>(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  
  // Modals
  const [showEventModal, setShowEventModal] = useState(false);
  const [showRoundModal, setShowRoundModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showQRCodes, setShowQRCodes] = useState(false);
  const [deleteConfirmEventId, setDeleteConfirmEventId] = useState<string | null>(null);
  
  // Edit states
  const [editingEvent, setEditingEvent] = useState<HitsterEvent | null>(null);
  const [editingRound, setEditingRound] = useState<EventRound | null>(null);
  const [editingTeam, setEditingTeam] = useState<EventTeam | null>(null);
  
  // Form fields
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventType, setEventType] = useState<'rounds' | 'theme-night'>('rounds');
  const [eventEnableTeamApp, setEventEnableTeamApp] = useState(false);
  const [eventAnnouncementText, setEventAnnouncementText] = useState('');
  const [eventIntroFlyerUrl, setEventIntroFlyerUrl] = useState('');
  const [eventGameRules, setEventGameRules] = useState<GameRulesConfig>(getDefaultGameRulesConfig());
  const [flyerUploading, setFlyerUploading] = useState(false);
  const flyerInputRef = useRef<HTMLInputElement>(null);
  const slideAssetInputRef = useRef<HTMLInputElement>(null);
  const [uploadingSlideId, setUploadingSlideId] = useState<string | null>(null);
  const [roundName, setRoundName] = useState('');
  const [roundSongs, setRoundSongs] = useState<EventSong[]>([]);
  const [roundNewTitle, setRoundNewTitle] = useState('');
  const [roundNewArtist, setRoundNewArtist] = useState('');
  const [roundNewYear, setRoundNewYear] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamMemberCount, setTeamMemberCount] = useState('');
  const [teamGenerationCategory, setTeamGenerationCategory] = useState<'' | HitsterGenerationPeriodId>('');
  const [draggedSongId, setDraggedSongId] = useState<string | null>(null);
  const [dropTargetSongId, setDropTargetSongId] = useState<string | null>(null);
  const [draggedRoundSongId, setDraggedRoundSongId] = useState<string | null>(null);
  const [dropTargetRoundSongId, setDropTargetRoundSongId] = useState<string | null>(null);
  const [draggedThemeSongId, setDraggedThemeSongId] = useState<string | null>(null);
  const [dropTargetThemeSongId, setDropTargetThemeSongId] = useState<string | null>(null);

  // Load events on mount
  useEffect(() => {
    loadEvents();
  }, []);

  const slideTypeLabels: Record<GameRulesSlideType, string> = {
    title: 'Titel',
    flyer: 'Flyer',
    text: 'Tekst',
    steps: 'Stappen',
    scoring: 'Punten',
    position: 'Positie',
    teams: 'Teams',
    final: 'Afsluiting'
  };

  const updateGameRuleSlides = (updater: (slides: GameRulesSlide[]) => GameRulesSlide[]) => {
    setEventGameRules(prev => ({
      ...prev,
      slides: updater([...(prev.slides || [])])
    }));
  };

  const updateGameRuleSlide = (slideId: string, updates: Partial<GameRulesSlide>) => {
    updateGameRuleSlides(slides => slides.map(slide => slide.id === slideId ? { ...slide, ...updates } : slide));
  };

  const moveGameRuleSlide = (slideId: string, direction: 'up' | 'down') => {
    updateGameRuleSlides(slides => {
      const index = slides.findIndex(slide => slide.id === slideId);
      if (index === -1) return slides;

      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= slides.length) return slides;

      const reordered = [...slides];
      const [slide] = reordered.splice(index, 1);
      reordered.splice(targetIndex, 0, slide);
      return reordered;
    });
  };

  const parseMultilineText = (value: string) => {
    return value
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);
  };

  const parseScoringItems = (value: string): GameRulesScoringItem[] => {
    return value
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        const separator = line.includes('|') ? '|' : '-';
        const [pointsPart, ...labelParts] = line.split(separator);
        return {
          points: (pointsPart || '').trim(),
          label: labelParts.join(separator).trim()
        };
      })
      .filter(item => item.points || item.label);
  };

  const formatMultilineText = (lines?: string[]) => (lines || []).join('\n');

  const formatScoringItems = (items?: GameRulesScoringItem[]) => {
    return (items || []).map(item => `${item.points} | ${item.label}`).join('\n');
  };

  const loadEvents = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/events`);
      if (response.ok) {
        const rawData = await response.json();
        const data: HitsterEvent[] = (rawData || []).map((event: HitsterEvent) => ({
          ...event,
          rounds: event.rounds || [],
          themeSongs: event.themeSongs || [],
        }));
        setEvents(data);
        if (data.length > 0 && !selectedEvent) {
          setSelectedEvent(data[0]);
          if (getEventType(data[0]) === 'rounds' && data[0].rounds?.length > 0) {
            setSelectedRound(data[0].rounds[0]);
          } else {
            setSelectedRound(null);
          }
        }
      }
    } catch (err) {
      console.error('Error loading events:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const eventStandings = selectedEvent ? getEventTeamStandings(selectedEvent) : [];
  const selectedEventHasScores = selectedEvent ? eventHasRoundScores(selectedEvent) : false;

  const showGameRulesOnDisplay = async (event: HitsterEvent) => {
    try {
      const response = await fetch(`${API_BASE}/api/display-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'rules',
          rulesEventId: event.id,
          isPlaying: false,
          showResults: false,
          isIntermission: false,
          waitingMessage: null,
          currentSong: null,
          eventName: event.name,
          roundName: '',
          roundNumber: 0,
          youtubePaused: false,
          youtubeVolume: 100
        })
      });

      if (!response.ok) {
        throw new Error('Display state could not be updated');
      }
    } catch (err) {
      console.error('Failed to show game rules on display:', err);
      window.open(`/game-rules/${event.id}`, '_blank');
    }
  };

  const closeGameRulesOnDisplay = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/display-state/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'hitster',
          rulesEventId: null
        })
      });

      if (!response.ok) {
        throw new Error('Display state could not be reset');
      }
    } catch (err) {
      console.error('Failed to close game rules on display:', err);
    }
  };

  // Event CRUD
  const openEventModal = (event?: HitsterEvent) => {
    if (event) {
      setEditingEvent(event);
      setEventName(event.name);
      setEventDate(event.date);
      setEventDescription(event.description || '');
      setEventType(getEventType(event));
      setEventEnableTeamApp(event.enableTeamApp || false);
      setEventAnnouncementText(event.announcementText || '');
      setEventIntroFlyerUrl(event.introFlyerUrl || '');
      setEventGameRules(normalizeGameRulesConfig(event.gameRules));
    } else {
      setEditingEvent(null);
      setEventName('');
      setEventDate(new Date().toISOString().split('T')[0]);
      setEventDescription('');
      setEventType('rounds');
      setEventEnableTeamApp(false);
      setEventAnnouncementText('');
      setEventIntroFlyerUrl('');
      setEventGameRules(getDefaultGameRulesConfig());
    }
    setShowEventModal(true);
  };

  // Flyer upload handler
  const handleFlyerUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setFlyerUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setEventIntroFlyerUrl(data.imageUrl);
      } else {
        console.error('Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setFlyerUploading(false);
      if (flyerInputRef.current) {
        flyerInputRef.current.value = '';
      }
    }
  };

  const startSlideIllustrationUpload = (slideId: string) => {
    setUploadingSlideId(slideId);
    slideAssetInputRef.current?.click();
  };

  const handleSlideIllustrationUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !uploadingSlideId) return;

    const targetSlideId = uploadingSlideId;
    setFlyerUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        updateGameRuleSlide(targetSlideId, { illustrationUrl: data.imageUrl });
      } else {
        console.error('Slide upload failed');
      }
    } catch (err) {
      console.error('Slide upload error:', err);
    } finally {
      setFlyerUploading(false);
      setUploadingSlideId(null);
      if (slideAssetInputRef.current) {
        slideAssetInputRef.current.value = '';
      }
    }
  };

  const saveEvent = async () => {
    if (!eventName.trim()) return;
    
    if (editingEvent) {
      // Update existing
      const updated = {
        ...editingEvent,
        name: eventName,
        date: eventDate,
        eventType,
        description: eventDescription,
        enableTeamApp: eventEnableTeamApp,
        announcementText: eventAnnouncementText || undefined,
        introFlyerUrl: eventIntroFlyerUrl || undefined,
        gameRules: eventGameRules,
        themeSongs: editingEvent.themeSongs || [],
        updatedAt: new Date().toISOString(),
      };
      try {
        await fetch(`${API_BASE}/api/events/${editingEvent.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updated),
        });
      } catch (err) {
        console.error('Error updating event:', err);
      }
      setEvents(events.map(e => e.id === updated.id ? updated : e));
      if (selectedEvent?.id === updated.id) {
        setSelectedEvent(updated);
      }
    } else {
      // Create new
      const newEvent: HitsterEvent = {
        id: `event-${Date.now()}`,
        name: eventName,
        date: eventDate || new Date().toISOString().split('T')[0],
        eventType,
        description: eventDescription,
        enableTeamApp: eventEnableTeamApp,
        announcementText: eventAnnouncementText || undefined,
        introFlyerUrl: eventIntroFlyerUrl || undefined,
        gameRules: eventGameRules,
        rounds: [],
        themeSongs: [],
        teams: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      try {
        const response = await fetch(`${API_BASE}/api/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newEvent),
        });
        if (response.ok) {
          const saved = await response.json();
          setEvents([...events, saved]);
          setSelectedEvent(saved);
        }
      } catch (err) {
        setEvents([...events, newEvent]);
        setSelectedEvent(newEvent);
      }
    }
    setShowEventModal(false);
  };

  const deleteEvent = async (eventId: string) => {
    try {
      await fetch(`${API_BASE}/api/events/${eventId}`, { method: 'DELETE' });
    } catch (err) {
      console.error('Error deleting event:', err);
    }
    const remaining = events.filter(e => e.id !== eventId);
    setEvents(remaining);
    if (selectedEvent?.id === eventId) {
      setSelectedEvent(remaining[0] || null);
      setSelectedRound(null);
    }
    setDeleteConfirmEventId(null);
  };

  const archiveEvent = async (eventId: string, archive: boolean) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    const updated = { ...event, archived: archive, updatedAt: new Date().toISOString() };
    try {
      await fetch(`${API_BASE}/api/events/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
    } catch (err) {
      console.error('Error archiving event:', err);
    }
    const newEvents = events.map(e => e.id === eventId ? updated : e);
    setEvents(newEvents);
    if (selectedEvent?.id === eventId) {
      if (archive && !showArchived) {
        const next = newEvents.find(e => !e.archived);
        setSelectedEvent(next || null);
        setSelectedRound(next?.rounds[0] || null);
      } else {
        setSelectedEvent(updated);
      }
    }
  };

  // Round CRUD
  const openRoundModal = (round?: EventRound) => {
    if (round) {
      setEditingRound(round);
      setRoundName(round.name);
      setRoundSongs(sortRoundSongs(round.songs || []));
    } else {
      setEditingRound(null);
      setRoundName('');
      setRoundSongs([]);
    }
    setRoundNewTitle('');
    setRoundNewArtist('');
    setRoundNewYear('');
    setShowRoundModal(true);
  };

  const addRoundSong = () => {
    const title = roundNewTitle.trim();
    const artist = roundNewArtist.trim();
    const year = parseInt(roundNewYear);
    if (!title || !artist || !year || isNaN(year)) return;
    const newSong: EventSong = {
      id: `song-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      title,
      artist,
      year,
      duration: 0,
      order: roundSongs.length + 1,
    };
    setRoundSongs(prev => [...prev, newSong]);
    setRoundNewTitle('');
    setRoundNewArtist('');
    setRoundNewYear('');
  };

  const reorderSongsById = (songs: EventSong[], sourceId: string, targetId: string) => {
    const sourceIndex = songs.findIndex(song => song.id === sourceId);
    const targetIndex = songs.findIndex(song => song.id === targetId);

    if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
      return songs;
    }

    const reorderedSongs = [...songs];
    const [movedSong] = reorderedSongs.splice(sourceIndex, 1);
    reorderedSongs.splice(targetIndex, 0, movedSong);

    return reorderedSongs.map((song, index) => ({
      ...song,
      order: index + 1,
    }));
  };

  const moveRoundSong = (songId: string, direction: 'up' | 'down') => {
    setRoundSongs(prev => {
      const currentIndex = prev.findIndex(song => song.id === songId);
      if (currentIndex === -1) return prev;

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const reordered = [...prev];
      const [movedSong] = reordered.splice(currentIndex, 1);
      reordered.splice(targetIndex, 0, movedSong);

      return reordered.map((song, index) => ({
        ...song,
        order: index + 1,
      }));
    });
  };

  const moveRoundSongByDrop = (sourceId: string, targetId: string) => {
    setRoundSongs(prev => reorderSongsById(prev, sourceId, targetId));
  };

  const removeRoundSong = (id: string) => {
    setRoundSongs(prev =>
      prev
        .filter(s => s.id !== id)
        .map((song, index) => ({
          ...song,
          order: index + 1,
        }))
    );
  };

  const saveRound = async () => {
    if (!selectedEvent || !roundName.trim()) return;
    
    let updatedEvent: HitsterEvent;
    
    if (editingRound) {
      const updatedRound = { ...editingRound, name: roundName, songs: roundSongs };
      updatedEvent = {
        ...selectedEvent,
        rounds: selectedEvent.rounds.map(r => r.id === editingRound.id ? updatedRound : r),
        updatedAt: new Date().toISOString(),
      };
      if (selectedRound?.id === editingRound.id) {
        setSelectedRound(updatedRound);
      }
    } else {
      const newRound: EventRound = {
        id: `round-${Date.now()}`,
        name: roundName,
        order: selectedEvent.rounds.length + 1,
        songs: roundSongs,
      };
      updatedEvent = {
        ...selectedEvent,
        rounds: [...selectedEvent.rounds, newRound],
        updatedAt: new Date().toISOString(),
      };
      setSelectedRound(newRound);
    }
    
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error saving round:', err);
    }
    
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setShowRoundModal(false);
  };

  const deleteRound = async (roundId: string) => {
    if (!selectedEvent) return;
    
    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.filter(r => r.id !== roundId),
      updatedAt: new Date().toISOString(),
    };
    
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error deleting round:', err);
    }
    
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    if (selectedRound?.id === roundId) {
      setSelectedRound(updatedEvent.rounds[0] || null);
    }
  };

  const applyEventUpdate = async (updatedEvent: HitsterEvent) => {
    try {
      await fetch(`${API_BASE}/api/events/${updatedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error updating event:', err);
    }

    setSelectedEvent(updatedEvent);
    setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };

  const addThemeSong = async () => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night') return;

    const title = roundNewTitle.trim();
    const artist = roundNewArtist.trim();
    const year = parseInt(roundNewYear);
    if (!title || !artist || !year || isNaN(year)) return;

    const existing = selectedEvent.themeSongs || [];
    const newSong: EventSong = {
      id: `song-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      title,
      artist,
      year,
      duration: 0,
      order: existing.length + 1,
    };

    const updatedEvent: HitsterEvent = {
      ...selectedEvent,
      themeSongs: [...existing, newSong],
      updatedAt: new Date().toISOString(),
    };

    await applyEventUpdate(updatedEvent);
    setRoundNewTitle('');
    setRoundNewArtist('');
    setRoundNewYear('');
  };

  const moveThemeSong = async (songId: string, direction: 'up' | 'down') => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night') return;

    const songs = [...(selectedEvent.themeSongs || [])];
    const currentIndex = songs.findIndex(song => song.id === songId);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= songs.length) return;

    const [movedSong] = songs.splice(currentIndex, 1);
    songs.splice(targetIndex, 0, movedSong);

    const normalizedSongs = songs.map((song, index) => ({ ...song, order: index + 1 }));
    await applyEventUpdate({
      ...selectedEvent,
      themeSongs: normalizedSongs,
      updatedAt: new Date().toISOString(),
    });
  };

  const moveThemeSongByDrop = async (sourceId: string, targetId: string) => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night') return;

    const currentSongs = selectedEvent.themeSongs || [];
    const normalizedSongs = reorderSongsById(currentSongs, sourceId, targetId);
    if (normalizedSongs === currentSongs) return;

    await applyEventUpdate({
      ...selectedEvent,
      themeSongs: normalizedSongs,
      updatedAt: new Date().toISOString(),
    });
  };

  const removeThemeSong = async (songId: string) => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night') return;

    const normalizedSongs = (selectedEvent.themeSongs || [])
      .filter(song => song.id !== songId)
      .map((song, index) => ({
        ...song,
        order: index + 1,
      }));

    await applyEventUpdate({
      ...selectedEvent,
      themeSongs: normalizedSongs,
      updatedAt: new Date().toISOString(),
    });
  };

  // Team CRUD
  const openTeamModal = (team?: EventTeam) => {
    if (team) {
      setEditingTeam(team);
      setTeamName(team.name);
      setTeamMemberCount(team.memberCount?.toString() || '');
      setTeamGenerationCategory(team.generationCategory || '');
    } else {
      setEditingTeam(null);
      setTeamName('');
      setTeamMemberCount('');
      setTeamGenerationCategory('');
    }
    setShowTeamModal(true);
  };

  const saveTeam = async () => {
    if (!selectedEvent || !teamName.trim()) return;
    
    let updatedEvent: HitsterEvent;
    
    if (editingTeam) {
      const updatedTeam: EventTeam = {
        ...editingTeam,
        name: teamName,
        memberCount: teamMemberCount ? parseInt(teamMemberCount) : undefined,
        generationCategory: teamGenerationCategory || undefined,
      };
      updatedEvent = {
        ...selectedEvent,
        teams: (selectedEvent.teams || []).map(t => t.id === editingTeam.id ? updatedTeam : t),
        updatedAt: new Date().toISOString(),
      };
    } else {
      const newTeam: EventTeam = {
        id: `team-${Date.now()}`,
        name: teamName,
        memberCount: teamMemberCount ? parseInt(teamMemberCount) : undefined,
        generationCategory: teamGenerationCategory || undefined,
      };
      updatedEvent = {
        ...selectedEvent,
        teams: [...(selectedEvent.teams || []), newTeam],
        updatedAt: new Date().toISOString(),
      };
    }
    
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error saving team:', err);
    }
    
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setShowTeamModal(false);
  };

  const deleteTeam = async (teamId: string) => {
    if (!selectedEvent) return;
    
    const updatedEvent = {
      ...selectedEvent,
      teams: (selectedEvent.teams || []).filter(t => t.id !== teamId),
      updatedAt: new Date().toISOString(),
    };
    
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error deleting team:', err);
    }
    
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };

  // Generate join codes for teams
  const generateJoinCodes = async () => {
    if (!selectedEvent) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/events/${selectedEvent.id}/generate-join-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.ok) {
        const data = await response.json();
        const updatedEvent = {
          ...selectedEvent,
          teams: data.teams,
        };
        setSelectedEvent(updatedEvent);
        setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
      }
    } catch (err) {
      console.error('Error generating join codes:', err);
    }
  };

  // Remove song from round
  const removeSong = async (songId: string) => {
    if (!selectedEvent || !selectedRound) return;
    
    const updatedRound = {
      ...selectedRound,
      songs: selectedRound.songs.filter(s => s.id !== songId),
    };
    
    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.map(r => r.id === selectedRound.id ? updatedRound : r),
      updatedAt: new Date().toISOString(),
    };
    
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error removing song:', err);
    }
    
    setSelectedRound(updatedRound);
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };

  const moveSongInSelectedRound = async (songId: string, direction: 'up' | 'down') => {
    if (!selectedEvent || !selectedRound) return;

    const currentIndex = selectedRound.songs.findIndex(song => song.id === songId);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= selectedRound.songs.length) return;

    const reorderedSongs = [...selectedRound.songs];
    const [movedSong] = reorderedSongs.splice(currentIndex, 1);
    reorderedSongs.splice(targetIndex, 0, movedSong);

    const normalizedSongs = reorderedSongs.map((song, index) => ({
      ...song,
      order: index + 1,
    }));

    const updatedRound = {
      ...selectedRound,
      songs: normalizedSongs,
    };

    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.map(round =>
        round.id === selectedRound.id ? updatedRound : round
      ),
      updatedAt: new Date().toISOString(),
    };

    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error moving song:', err);
      return;
    }

    setSelectedRound(updatedRound);
    setSelectedEvent(updatedEvent);
    setEvents(events.map(event => (event.id === updatedEvent.id ? updatedEvent : event)));
  };

  const moveSongInSelectedRoundByDrop = async (sourceId: string, targetId: string) => {
    if (!selectedEvent || !selectedRound) return;

    const normalizedSongs = reorderSongsById(selectedRound.songs, sourceId, targetId);
    if (normalizedSongs === selectedRound.songs) return;

    const updatedRound = {
      ...selectedRound,
      songs: normalizedSongs,
    };

    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.map(round =>
        round.id === selectedRound.id ? updatedRound : round
      ),
      updatedAt: new Date().toISOString(),
    };

    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error moving song by drag and drop:', err);
      return;
    }

    setSelectedRound(updatedRound);
    setSelectedEvent(updatedEvent);
    setEvents(events.map(event => (event.id === updatedEvent.id ? updatedEvent : event)));
  };

  const handleSongDragStart = (songId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.dataTransfer.effectAllowed = 'move';
    setDraggedSongId(songId);
  };

  const handleSongDragOver = (songId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (songId !== draggedSongId) {
      setDropTargetSongId(songId);
    }
  };

  const handleSongDrop = async (targetSongId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (draggedSongId && draggedSongId !== targetSongId) {
      await moveSongInSelectedRoundByDrop(draggedSongId, targetSongId);
    }

    setDraggedSongId(null);
    setDropTargetSongId(null);
  };

  const handleRoundSongDragStart = (songId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.dataTransfer.effectAllowed = 'move';
    setDraggedRoundSongId(songId);
  };

  const handleRoundSongDragOver = (songId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (songId !== draggedRoundSongId) {
      setDropTargetRoundSongId(songId);
    }
  };

  const handleRoundSongDrop = (targetSongId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (draggedRoundSongId && draggedRoundSongId !== targetSongId) {
      moveRoundSongByDrop(draggedRoundSongId, targetSongId);
    }

    setDraggedRoundSongId(null);
    setDropTargetRoundSongId(null);
  };

  if (loading) {
    return (
      <div className="events-page">
        <div className="loading">Laden...</div>
      </div>
    );
  }

  return (
    <div className="events-page">
      <header className="events-header">
        <h1>📅 Evenementen Beheer</h1>
        <div className="header-actions">
          <a href="/camera-qr" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-md" style={{textDecoration:'none'}}>
            📲 QR-codes per team
          </a>
          <Button variant="secondary" onClick={() => navigate('/spotify-dashboard')}>
            🎧 Dashboard
          </Button>
        </div>
      </header>

      <div className="events-layout">
        {/* Left: Events list */}
        <div className="events-sidebar">
          <div className="sidebar-header">
            <h2>Evenementen</h2>
            <button className="add-btn" onClick={() => openEventModal()}>
              + Nieuw
            </button>
          </div>
          
          <div className="events-list">
            {events.filter(e => showArchived ? e.archived : !e.archived).map(event => (
              <div 
                key={event.id}
                className={`event-item ${selectedEvent?.id === event.id ? 'selected' : ''} ${event.archived ? 'archived' : ''}`}
                onClick={() => {
                  setSelectedEvent(event);
                  if (getEventType(event) === 'rounds') {
                    setSelectedRound(event.rounds[0] || null);
                  } else {
                    setSelectedRound(null);
                  }
                }}
              >
                <div className="event-item-info">
                  <div className="event-item-name">{event.name}</div>
                  <div className="event-item-date">{formatDate(event.date)}</div>
                  <div className="event-item-stats">
                    {getEventType(event) === 'theme-night'
                      ? `${getEventSongCount(event)} nummers • Thema-avond`
                      : `${event.rounds.length} rondes • ${getEventSongCount(event)} nummers`} • {(event.teams || []).length} teams
                  </div>
                </div>
                <div className="event-item-actions">
                  <button onClick={(e) => { e.stopPropagation(); openEventModal(event); }}>✏️</button>
                  {event.archived
                    ? <button title="Dearchiveren" onClick={(e) => { e.stopPropagation(); void archiveEvent(event.id, false); }}>📤</button>
                    : <button title="Archiveren" onClick={(e) => { e.stopPropagation(); void archiveEvent(event.id, true); }}>📦</button>
                  }
                  <button onClick={(e) => { e.stopPropagation(); setDeleteConfirmEventId(event.id); }}>🗑️</button>
                </div>
              </div>
            ))}
            
            {events.filter(e => showArchived ? e.archived : !e.archived).length === 0 && (
              <div className="empty-message">
                {showArchived ? 'Geen gearchiveerde evenementen.' : 'Geen evenementen. Maak je eerste evenement aan!'}
              </div>
            )}

            <div className="archive-toggle">
              <button onClick={() => setShowArchived(v => !v)}>
                {showArchived ? '← Terug naar actief' : `📦 Archief (${events.filter(e => e.archived).length})`}
              </button>
            </div>
          </div>
        </div>

        {/* Middle: Event details */}
        {selectedEvent && (
          <div className="event-details">
            <div className="event-actions-bar">
              <button 
                className="action-btn rules-btn"
                onClick={() => void showGameRulesOnDisplay(selectedEvent)}
              >
                📖 Speluitleg op groot scherm
              </button>
              <button
                className="action-btn"
                onClick={() => void closeGameRulesOnDisplay()}
              >
                ✕ Sluit speluitleg
              </button>
              <button 
                className="action-btn scoreboard-btn"
                onClick={() => window.open(`/scoreboard/${selectedEvent.id}`, '_blank')}
              >
                🏆 Scorebord
                            </button>
              <button 
                className="action-btn print-years-btn"
                onClick={() => navigate(`/print-start-years/${selectedEvent.id}`)}
              >
                🖨️ Print startjaargetallen
              </button>
            </div>

            <div className="details-section">
              <div className="section-header">
                <h2>👥 Teams {selectedEvent.enableTeamApp && <span className="team-app-badge">📱 App actief</span>}</h2>
                <div className="section-header-actions">
                  {selectedEvent.enableTeamApp && (selectedEvent.teams || []).length > 0 && (
                    <button className="qr-btn small" onClick={() => setShowQRCodes(true)}>
                      📲 QR Codes
                    </button>
                  )}
                  <button className="add-btn small" onClick={() => openTeamModal()}>
                    + Team
                  </button>
                </div>
              </div>
              
              <div className="teams-grid">
                {(selectedEvent.teams || []).map(team => (
                  <div key={team.id} className="team-card">
                    <div className="team-card-info">
                      <span className="team-card-name">{team.name}</span>
                      {team.memberCount && (
                        <span className="team-card-members">{team.memberCount} leden</span>
                      )}
                      {team.generationCategory && (
                        <span className="team-card-generation">{formatGenerationCategory(team.generationCategory)}</span>
                      )}
                      {selectedEvent.enableTeamApp && team.joinCode && (
                        <span className="team-card-code">Code: {team.joinCode}</span>
                      )}
                    </div>
                    <div className="team-card-actions">
                      <button onClick={() => openTeamModal(team)}>✏️</button>
                      <button onClick={() => deleteTeam(team.id)}>✕</button>
                    </div>
                  </div>
                ))}
                
                {(!selectedEvent.teams || selectedEvent.teams.length === 0) && (
                  <div className="empty-message small">
                    Nog geen teams toegevoegd
                  </div>
                )}
              </div>
            </div>

            {getEventType(selectedEvent) === 'rounds' ? (
              <>
                <div className="details-section">
                  <div className="section-header">
                    <h2>🎯 Rondes</h2>
                    <button className="add-btn small" onClick={() => openRoundModal()}>
                      + Ronde
                    </button>
                  </div>

                  <div className="rounds-grid">
                    {selectedEvent.rounds.map(round => (
                      <div
                        key={round.id}
                        className={`round-card ${selectedRound?.id === round.id ? 'selected' : ''}`}
                        onClick={() => setSelectedRound(round)}
                      >
                        <div className="round-card-info">
                          <span className="round-card-name">{round.name}</span>
                          <span className="round-card-count">{round.songs.length} nummers</span>
                        </div>
                        <div className="round-card-actions">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/round-scoring/${selectedEvent.id}/${round.id}`);
                            }}
                            title="Scores invoeren"
                            className="score-btn"
                          >
                            📝
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); openRoundModal(round); }}>✏️</button>
                          <button onClick={(e) => { e.stopPropagation(); deleteRound(round.id); }}>✕</button>
                        </div>
                      </div>
                    ))}

                    {selectedEvent.rounds.length === 0 && (
                      <div className="empty-message small">
                        Nog geen rondes toegevoegd
                      </div>
                    )}
                  </div>
                </div>

                <div className="details-section event-scores-section">
                  <div className="section-header event-scores-header">
                    <div>
                      <h2>🏆 Scores</h2>
                      <p className="section-subtitle">Per team en per ronde binnen dit evenement</p>
                    </div>
                    <div className="event-scores-meta">
                      <span>{(selectedEvent.teams || []).length} teams</span>
                      <span>{selectedEvent.rounds.length} rondes</span>
                    </div>
                  </div>

                  {selectedEventHasScores ? (
                    <div className="event-scores-table-wrapper">
                      <table className="event-scores-table">
                        <thead>
                          <tr>
                            <th className="event-scores-team-col">Team</th>
                            {selectedEvent.rounds.map(round => (
                              <th key={round.id} className="event-scores-round-col">{round.name}</th>
                            ))}
                            <th className="event-scores-total-col">Totaal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eventStandings.map(standing => (
                            <tr key={standing.team.id}>
                              <td className="event-scores-team-col">
                                <div className="event-score-team-cell">
                                  <span className="event-score-rank">{standing.rank}.</span>
                                  <span className="event-score-team-name">{standing.team.name}</span>
                                </div>
                              </td>
                              {standing.roundScores.map(roundScore => (
                                <td key={roundScore.roundId} className="event-scores-round-col">
                                  <span className="event-score-value">{roundScore.score}</span>
                                  <span className="event-score-label">pt</span>
                                </td>
                              ))}
                              <td className="event-scores-total-col">
                                <strong>{standing.totalScore} pt</strong>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="event-scores-empty-state">
                      <div className="event-scores-empty-icon">🏁</div>
                      <div>
                        <h3>Nog geen scores beschikbaar</h3>
                        <p>Voer eerst scores in bij een ronde. Daarna verschijnen hier automatisch de totaalscores en scores per ronde.</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="details-section">
                <div className="section-header">
                  <h2>🎵 Thema-lijst</h2>
                </div>
                <div className="round-song-form">
                  <input
                    type="text"
                    value={roundNewArtist}
                    onChange={e => setRoundNewArtist(e.target.value)}
                    placeholder="Artiest"
                    className="round-song-input"
                  />
                  <input
                    type="text"
                    value={roundNewTitle}
                    onChange={e => setRoundNewTitle(e.target.value)}
                    placeholder="Titel"
                    className="round-song-input"
                    onKeyDown={e => { if (e.key === 'Enter') void addThemeSong(); }}
                  />
                  <input
                    type="number"
                    value={roundNewYear}
                    onChange={e => setRoundNewYear(e.target.value)}
                    placeholder="Jaar"
                    className="round-song-input round-song-year"
                    onKeyDown={e => { if (e.key === 'Enter') void addThemeSong(); }}
                  />
                  <button
                    className="round-song-add-btn"
                    onClick={() => void addThemeSong()}
                    disabled={!roundNewTitle.trim() || !roundNewArtist.trim() || !roundNewYear}
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Right: Songs in selected round */}
        {selectedRound && selectedEvent && getEventType(selectedEvent) === 'rounds' && (
          <div className="songs-panel">
            <div className="songs-header">
              <h2>🎵 {selectedRound.name}</h2>
              <span className="songs-count">{selectedRound.songs.length} nummers</span>
            </div>
            
            <div className="songs-info">
              <p>Voeg nummers toe via het <strong>Dashboard</strong></p>
              <Button variant="primary" onClick={() => navigate('/spotify-dashboard')}>
                🎧 Naar Dashboard
              </Button>
            </div>
            
            <div className="songs-list">
              {selectedRound.songs.map((song, index) => (
                <div
                  key={song.id}
                  className={`song-item ${draggedSongId === song.id ? 'is-dragging' : ''} ${dropTargetSongId === song.id ? 'is-drop-target' : ''}`}
                  draggable
                  onDragStart={(event) => handleSongDragStart(song.id, event)}
                  onDragOver={(event) => handleSongDragOver(song.id, event)}
                  onDrop={(event) => void handleSongDrop(song.id, event)}
                  onDragEnd={() => {
                    setDraggedSongId(null);
                    setDropTargetSongId(null);
                  }}
                >
                  <span className="song-number">{index + 1}</span>
                  {song.imageUrl && (
                    <img src={song.imageUrl} alt="" className="song-thumb" />
                  )}
                  <div className="song-info">
                    <div className="song-title">{song.title}</div>
                    <div className="song-artist">{song.artist} • {song.year}</div>
                  </div>
                  <div className="song-move-controls">
                    <button
                      className="move-btn"
                      onClick={() => moveSongInSelectedRound(song.id, 'up')}
                      disabled={index === 0}
                      title="Naar boven"
                    >
                      ↑
                    </button>
                    <button
                      className="move-btn"
                      onClick={() => moveSongInSelectedRound(song.id, 'down')}
                      disabled={index === selectedRound.songs.length - 1}
                      title="Naar beneden"
                    >
                      ↓
                    </button>
                  </div>
                  <button className="remove-btn" onClick={() => removeSong(song.id)}>✕</button>
                </div>
              ))}
              
              {selectedRound.songs.length === 0 && (
                <div className="empty-message">
                  Geen nummers in deze ronde
                </div>
              )}
            </div>
          </div>
        )}

        {selectedEvent && getEventType(selectedEvent) === 'theme-night' && (
          <div className="songs-panel">
            <div className="songs-header">
              <h2>🎵 {selectedEvent.name} - Thema-lijst</h2>
              <span className="songs-count">{(selectedEvent.themeSongs || []).length} nummers</span>
            </div>

            <div className="songs-list">
              {(selectedEvent.themeSongs || []).map((song, index) => (
                <div
                  key={song.id}
                  className={`song-item ${draggedThemeSongId === song.id ? 'is-dragging' : ''} ${dropTargetThemeSongId === song.id ? 'is-drop-target' : ''}`}
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    setDraggedThemeSongId(song.id);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    if (song.id !== draggedThemeSongId) {
                      setDropTargetThemeSongId(song.id);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (draggedThemeSongId && draggedThemeSongId !== song.id) {
                      void moveThemeSongByDrop(draggedThemeSongId, song.id);
                    }
                    setDraggedThemeSongId(null);
                    setDropTargetThemeSongId(null);
                  }}
                  onDragEnd={() => {
                    setDraggedThemeSongId(null);
                    setDropTargetThemeSongId(null);
                  }}
                >
                  <span className="song-number">{index + 1}</span>
                  <div className="song-info">
                    <div className="song-title">{song.title}</div>
                    <div className="song-artist">{song.artist} • {song.year}</div>
                  </div>
                  <div className="song-move-controls">
                    <button
                      className="move-btn"
                      onClick={() => void moveThemeSong(song.id, 'up')}
                      disabled={index === 0}
                      title="Naar boven"
                    >
                      ↑
                    </button>
                    <button
                      className="move-btn"
                      onClick={() => void moveThemeSong(song.id, 'down')}
                      disabled={index === (selectedEvent.themeSongs || []).length - 1}
                      title="Naar beneden"
                    >
                      ↓
                    </button>
                  </div>
                  <button className="remove-btn" onClick={() => void removeThemeSong(song.id)}>✕</button>
                </div>
              ))}

              {(selectedEvent.themeSongs || []).length === 0 && (
                <div className="empty-message">
                  Nog geen nummers toegevoegd aan deze thema-avond
                </div>
              )}
            </div>
          </div>
        )}

        {/* No event selected */}
        {!selectedEvent && (
          <div className="no-selection">
            <p>Selecteer of maak een evenement aan</p>
          </div>
        )}
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingEvent ? '✏️ Evenement bewerken' : '📅 Nieuw evenement'}</h2>
            
            <div className="modal-field">
              <label>Naam</label>
              <input
                type="text"
                value={eventName}
                onChange={e => setEventName(e.target.value)}
                placeholder="bijv. Hitsteravond 18 april"
                autoFocus
              />
            </div>
            
            <div className="modal-field">
              <label>Datum</label>
              <input
                type="date"
                value={eventDate}
                onChange={e => setEventDate(e.target.value)}
              />
            </div>

            <div className="modal-field">
              <label>Type avond</label>
              <select
                value={eventType}
                onChange={e => setEventType(e.target.value as 'rounds' | 'theme-night')}
              >
                <option value="rounds">Hitster met rondes</option>
                <option value="theme-night">Thema-avond (zonder rondes)</option>
              </select>
            </div>
            
            <div className="modal-field">
              <label>Beschrijving (optioneel)</label>
              <textarea
                value={eventDescription}
                onChange={e => setEventDescription(e.target.value)}
                placeholder="Extra informatie..."
              />
            </div>
            
            <div className="modal-field checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={eventEnableTeamApp}
                  onChange={e => setEventEnableTeamApp(e.target.checked)}
                />
                <span>📱 Team App inschakelen</span>
              </label>
              <p className="field-help">Teams kunnen op hun telefoon hun kaarten bekijken en plaatsen</p>
            </div>

            <div className="modal-field">
              <label>📢 Aankondigingstekst (scorebord)</label>
              <p className="field-help">Deze tekst verschijnt als card naast het scorebord op de scores-pagina</p>
              <textarea
                value={eventAnnouncementText}
                onChange={e => setEventAnnouncementText(e.target.value)}
                placeholder="Bijv. 'Volgende Hitsteravond: 15 mei 2026 — reserveer je plek!'"
                rows={4}
              />
            </div>

            <div className="modal-field">
              <label>📄 Introductieflyer (optioneel)</label>
              <p className="field-help">Upload een afbeelding die getoond wordt bij de speluitleg</p>
              <input
                type="file"
                ref={flyerInputRef}
                accept="image/*"
                onChange={handleFlyerUpload}
                style={{ display: 'none' }}
              />
              <div className="flyer-upload-row">
                <button
                  type="button"
                  className="upload-btn"
                  onClick={() => flyerInputRef.current?.click()}
                  disabled={flyerUploading}
                >
                  {flyerUploading ? '⏳ Uploaden...' : '📤 Afbeelding kiezen'}
                </button>
                {eventIntroFlyerUrl && (
                  <div className="flyer-preview">
                    <img src={eventIntroFlyerUrl} alt="Flyer preview" />
                    <button
                      type="button"
                      className="remove-flyer-btn"
                      onClick={() => setEventIntroFlyerUrl('')}
                      title="Verwijder flyer"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-field">
              <label>📖 Speluitleg aanpassen</label>
              <p className="field-help">Je kunt placeholders gebruiken: {'{eventName}'}, {'{roundCount}'}, {'{songCount}'}, {'{teamCount}'}</p>
            </div>

            <input
              type="file"
              ref={slideAssetInputRef}
              accept=".svg,image/svg+xml"
              onChange={handleSlideIllustrationUpload}
              style={{ display: 'none' }}
            />

            <div className="game-rules-editor">
              {(eventGameRules.slides || []).map((slide, index, allSlides) => (
                <div key={slide.id} className="game-rule-slide-card">
                  <div className="game-rule-slide-header">
                    <div>
                      <div className="game-rule-slide-index">Slide {index + 1}</div>
                      <h3>{slideTypeLabels[slide.type]}</h3>
                    </div>
                    <div className="game-rule-slide-actions">
                      <button type="button" onClick={() => moveGameRuleSlide(slide.id, 'up')} disabled={index === 0}>↑</button>
                      <button type="button" onClick={() => moveGameRuleSlide(slide.id, 'down')} disabled={index === allSlides.length - 1}>↓</button>
                    </div>
                  </div>

                  {slide.type !== 'flyer' && (
                    <div className="modal-field compact-field">
                      <label>Icoon</label>
                      <input
                        type="text"
                        value={slide.icon || ''}
                        onChange={e => updateGameRuleSlide(slide.id, { icon: e.target.value })}
                        placeholder="Bijv. 🎵"
                      />
                    </div>
                  )}

                  <div className="modal-field compact-field">
                    <label>SVG illustratie</label>
                    <div className="slide-illustration-upload-row">
                      <button
                        type="button"
                        className="upload-btn"
                        onClick={() => startSlideIllustrationUpload(slide.id)}
                        disabled={flyerUploading}
                      >
                        {flyerUploading && uploadingSlideId === slide.id ? '⏳ Uploaden...' : '📤 SVG kiezen'}
                      </button>
                      {slide.illustrationUrl && (
                        <div className="slide-illustration-preview">
                          <img src={slide.illustrationUrl} alt="Slide illustratie" />
                          <button
                            type="button"
                            className="remove-flyer-btn"
                            onClick={() => updateGameRuleSlide(slide.id, { illustrationUrl: undefined })}
                            title="Verwijder SVG"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {slide.type === 'title' && (
                    <>
                      <div className="modal-field compact-field">
                        <label>Titel</label>
                        <input
                          type="text"
                          value={slide.title || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="modal-field compact-field">
                        <label>Subtitel</label>
                        <input
                          type="text"
                          value={slide.subtitle || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { subtitle: e.target.value })}
                        />
                      </div>
                      <div className="modal-field compact-field">
                        <label>Navigatiehint</label>
                        <input
                          type="text"
                          value={slide.hintText || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { hintText: e.target.value })}
                        />
                      </div>
                    </>
                  )}

                  {slide.type === 'flyer' && (
                    <p className="game-rule-slide-note">Deze slide toont automatisch de introductieflyer van het event als die is geupload.</p>
                  )}

                  {(slide.type === 'text' || slide.type === 'steps') && (
                    <>
                      <div className="modal-field compact-field">
                        <label>Titel</label>
                        <input
                          type="text"
                          value={slide.title || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="modal-field compact-field">
                        <label>{slide.type === 'steps' ? 'Stappen' : 'Tekstregels'}</label>
                        <textarea
                          value={formatMultilineText(slide.lines)}
                          onChange={e => updateGameRuleSlide(slide.id, { lines: parseMultilineText(e.target.value) })}
                          rows={slide.type === 'steps' ? 5 : 4}
                          placeholder="Elke regel wordt apart weergegeven"
                        />
                      </div>
                    </>
                  )}

                  {slide.type === 'scoring' && (
                    <>
                      <div className="modal-field compact-field">
                        <label>Titel</label>
                        <input
                          type="text"
                          value={slide.title || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="modal-field compact-field">
                        <label>Puntenregels</label>
                        <textarea
                          value={formatScoringItems(slide.items)}
                          onChange={e => updateGameRuleSlide(slide.id, { items: parseScoringItems(e.target.value) })}
                          rows={6}
                          placeholder="Bijv. 1 pt | Titel correct"
                        />
                      </div>
                    </>
                  )}

                  {slide.type === 'position' && (
                    <>
                      <div className="modal-field compact-field">
                        <label>Titel</label>
                        <input
                          type="text"
                          value={slide.title || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="game-rule-grid">
                        <div className="modal-field compact-field">
                          <label>Links label</label>
                          <input type="text" value={slide.leftLabel || ''} onChange={e => updateGameRuleSlide(slide.id, { leftLabel: e.target.value })} />
                        </div>
                        <div className="modal-field compact-field">
                          <label>Links beschrijving</label>
                          <input type="text" value={slide.leftDescription || ''} onChange={e => updateGameRuleSlide(slide.id, { leftDescription: e.target.value })} />
                        </div>
                        <div className="modal-field compact-field">
                          <label>Midden label</label>
                          <input type="text" value={slide.centerLabel || ''} onChange={e => updateGameRuleSlide(slide.id, { centerLabel: e.target.value })} />
                        </div>
                        <div className="modal-field compact-field">
                          <label>Rechts label</label>
                          <input type="text" value={slide.rightLabel || ''} onChange={e => updateGameRuleSlide(slide.id, { rightLabel: e.target.value })} />
                        </div>
                        <div className="modal-field compact-field">
                          <label>Rechts beschrijving</label>
                          <input type="text" value={slide.rightDescription || ''} onChange={e => updateGameRuleSlide(slide.id, { rightDescription: e.target.value })} />
                        </div>
                      </div>
                      <div className="modal-field compact-field">
                        <label>Tekst onder de kaart</label>
                        <input type="text" value={slide.footer || ''} onChange={e => updateGameRuleSlide(slide.id, { footer: e.target.value })} />
                      </div>
                    </>
                  )}

                  {slide.type === 'teams' && (
                    <>
                      <div className="modal-field compact-field">
                        <label>Titel</label>
                        <input
                          type="text"
                          value={slide.title || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="modal-field compact-field">
                        <label>Lege teams-tekst</label>
                        <input
                          type="text"
                          value={slide.emptyMessage || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { emptyMessage: e.target.value })}
                        />
                      </div>
                    </>
                  )}

                  {slide.type === 'final' && (
                    <>
                      <div className="modal-field compact-field">
                        <label>Titel</label>
                        <input
                          type="text"
                          value={slide.title || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="modal-field compact-field">
                        <label>Samenvatting</label>
                        <input
                          type="text"
                          value={slide.summary || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { summary: e.target.value })}
                        />
                      </div>
                      <div className="modal-field compact-field">
                        <label>Boodschap</label>
                        <input
                          type="text"
                          value={slide.message || ''}
                          onChange={e => updateGameRuleSlide(slide.id, { message: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowEventModal(false)}>
                Annuleren
              </Button>
              <Button variant="success" onClick={saveEvent} disabled={!eventName.trim()}>
                ✓ {editingEvent ? 'Opslaan' : 'Aanmaken'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Round Modal */}
      {showRoundModal && (
        <div className="modal-overlay" onClick={() => setShowRoundModal(false)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            <h2>{editingRound ? '✏️ Ronde bewerken' : '🎯 Nieuwe ronde'}</h2>
            
            <div className="modal-field">
              <label>Naam</label>
              <input
                type="text"
                value={roundName}
                onChange={e => setRoundName(e.target.value)}
                placeholder="bijv. Ronde 1 - 80s Hits"
                autoFocus
              />
            </div>

            <div className="modal-field">
              <label>🎵 Nummers toevoegen</label>
              <div className="round-song-form">
                <input
                  type="text"
                  value={roundNewArtist}
                  onChange={e => setRoundNewArtist(e.target.value)}
                  placeholder="Artiest"
                  className="round-song-input"
                />
                <input
                  type="text"
                  value={roundNewTitle}
                  onChange={e => setRoundNewTitle(e.target.value)}
                  placeholder="Titel"
                  className="round-song-input"
                  onKeyDown={e => { if (e.key === 'Enter') addRoundSong(); }}
                />
                <input
                  type="number"
                  value={roundNewYear}
                  onChange={e => setRoundNewYear(e.target.value)}
                  placeholder="Jaar"
                  className="round-song-input round-song-year"
                  onKeyDown={e => { if (e.key === 'Enter') addRoundSong(); }}
                />
                <button
                  className="round-song-add-btn"
                  onClick={addRoundSong}
                  disabled={!roundNewTitle.trim() || !roundNewArtist.trim() || !roundNewYear}
                >
                  +
                </button>
              </div>
            </div>

            {roundSongs.length > 0 && (
              <div className="round-songs-list">
                {roundSongs.map((song, index) => (
                  <div
                    key={song.id}
                    className={`round-song-item ${draggedRoundSongId === song.id ? 'is-dragging' : ''} ${dropTargetRoundSongId === song.id ? 'is-drop-target' : ''}`}
                    draggable
                    onDragStart={(event) => handleRoundSongDragStart(song.id, event)}
                    onDragOver={(event) => handleRoundSongDragOver(song.id, event)}
                    onDrop={(event) => handleRoundSongDrop(song.id, event)}
                    onDragEnd={() => {
                      setDraggedRoundSongId(null);
                      setDropTargetRoundSongId(null);
                    }}
                  >
                    <span className="round-song-num">{index + 1}</span>
                    <span className="round-song-info">{song.artist} — {song.title} ({song.year})</span>
                    <div className="round-song-move-controls">
                      <button
                        className="round-song-move"
                        onClick={() => moveRoundSong(song.id, 'up')}
                        disabled={index === 0}
                        title="Naar boven"
                      >
                        ↑
                      </button>
                      <button
                        className="round-song-move"
                        onClick={() => moveRoundSong(song.id, 'down')}
                        disabled={index === roundSongs.length - 1}
                        title="Naar beneden"
                      >
                        ↓
                      </button>
                    </div>
                    <button className="round-song-remove" onClick={() => removeRoundSong(song.id)}>✕</button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowRoundModal(false)}>
                Annuleren
              </Button>
              <Button variant="success" onClick={saveRound} disabled={!roundName.trim()}>
                ✓ {editingRound ? 'Opslaan' : 'Toevoegen'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && (
        <div className="modal-overlay" onClick={() => setShowTeamModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingTeam ? '✏️ Team bewerken' : '👥 Nieuw team'}</h2>
            
            <div className="modal-field">
              <label>Teamnaam</label>
              <input
                type="text"
                value={teamName}
                onChange={e => setTeamName(e.target.value)}
                placeholder="bijv. De Muzikanten"
                autoFocus
              />
            </div>
            
            <div className="modal-field">
              <label>Aantal leden (optioneel)</label>
              <input
                type="number"
                min="1"
                value={teamMemberCount}
                onChange={e => setTeamMemberCount(e.target.value)}
                placeholder="bijv. 4"
              />
            </div>

            <div className="modal-field">
              <label>Generatiecategorie (optioneel)</label>
              <select
                value={teamGenerationCategory}
                onChange={e => setTeamGenerationCategory((e.target.value as HitsterGenerationPeriodId) || '')}
              >
                <option value="">Geen koppeling</option>
                {TEAM_GENERATION_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>
            
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowTeamModal(false)}>
                Annuleren
              </Button>
              <Button variant="success" onClick={saveTeam} disabled={!teamName.trim()}>
                ✓ {editingTeam ? 'Opslaan' : 'Toevoegen'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmEventId && (() => {
        const ev = events.find(e => e.id === deleteConfirmEventId);
        return (
          <div className="modal-overlay" onClick={() => setDeleteConfirmEventId(null)}>
            <div className="modal confirm-modal" onClick={e => e.stopPropagation()}>
              <h2>🗑️ Evenement verwijderen</h2>
              <p>Weet je zeker dat je <strong>{ev?.name}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.</p>
              <div className="modal-actions">
                <Button variant="secondary" onClick={() => setDeleteConfirmEventId(null)}>
                  Annuleren
                </Button>
                <Button variant="danger" onClick={() => void deleteEvent(deleteConfirmEventId)}>
                  🗑️ Verwijderen
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Team QR Codes Modal */}
      {showQRCodes && selectedEvent && (
        <TeamQRCodes
          teams={selectedEvent.teams || []}
          eventId={selectedEvent.id}
          onClose={() => setShowQRCodes(false)}
          onGenerateCodes={generateJoinCodes}
        />
      )}
    </div>
  );
};

export default Events;
